import React, { useEffect, useState, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Wallet, RefreshCw, ChevronDown, ChevronRight, CheckCircle2, Clock, AlertTriangle, XCircle, Calculator, Users } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { fmtMoney as fmt } from "@/lib/format";

const LEVEL_COLORS = {
  p1_starter:      "bg-amber-100 text-amber-800 border-amber-300",
  p2_professional: "bg-blue-100 text-blue-800 border-blue-300",
  p3_senior:       "bg-violet-100 text-violet-800 border-violet-300",
  p4_partner:      "bg-prime/20 text-prime border-prime/40",
};

const STATUS_META = {
  paid:     { color: "text-emerald-600", icon: CheckCircle2, label: "Pagato" },
  pending:  { color: "text-amber-600",   icon: Clock,        label: "In attesa" },
  late:     { color: "text-orange-600",  icon: AlertTriangle, label: "In ritardo" },
  not_paid: { color: "text-rose-600",    icon: XCircle,      label: "Non pagato" },
};

function monthOptions() {
  const out = [];
  const now = new Date();
  for (let i = 0; i < 18; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const v = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    const label = d.toLocaleString("de-CH", { month: "long", year: "numeric" });
    out.push({ v, label });
  }
  return out;
}

export default function BuchhaltungPage() {
  const [month, setMonth] = useState(monthOptions()[0].v);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/accounting/buchhaltung?month=${month}`);
      setData(r.data);
    } catch (e) {
      toast.error("Errore: " + (e.response?.data?.detail || e.message));
    } finally { setLoading(false); }
  }, [month]);
  useEffect(() => { load(); }, [load]);

  const openDetail = async (consultant_id) => {
    if (expandedId === consultant_id) {
      setExpandedId(null);
      setDetail(null);
      return;
    }
    setExpandedId(consultant_id);
    try {
      const r = await api.get(`/accounting/consultant/${consultant_id}?month=${month}`);
      setDetail(r.data);
    } catch {
      toast.error("Errore caricamento dettaglio");
    }
  };

  const markCompanyPaid = async (split_id, status) => {
    try {
      await api.patch(`/accounting/splits/${split_id}/company-payment`, { status });
      toast.success(`Status compagnia: ${status}`);
      if (expandedId) openDetail(expandedId);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const payConsultant = async (split_id) => {
    if (!window.confirm("Conferma pagamento al consulente?")) return;
    try {
      await api.patch(`/accounting/splits/${split_id}/pay-consultant`);
      toast.success("Consulente pagato");
      if (expandedId) openDetail(expandedId);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const unlockReserve = async (split_id) => {
    const note = window.prompt("Nota (es. Storno-Frist abgelaufen)", "Periodo storni superato, no claim");
    if (note === null) return;
    try {
      await api.patch(`/accounting/splits/${split_id}/unlock-reserve`, { note });
      toast.success("Reserve sbloccato");
      if (expandedId) openDetail(expandedId);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const rebuild = async () => {
    if (!window.confirm("Rigenera tutti gli split commissioni (operazione admin)?")) return;
    setLoading(true);
    try {
      const r = await api.post("/accounting/rebuild-splits");
      toast.success(`Splits aggiornati: ${r.data.processed}`);
      load();
    } catch (e) { toast.error("Errore rebuild"); } finally { setLoading(false); }
  };

  const t = data?.totals || {};

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 max-w-7xl mx-auto" data-testid="buchhaltung-page">
      <div className="text-xs text-prime uppercase tracking-widest">/ Provisionen / Buchhaltung</div>

      <div className="flex justify-between items-start flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl text-stone-900 flex items-center gap-2">
            <Calculator className="w-7 h-7 text-prime" strokeWidth={1.6} />
            Buchhaltung — Provisioni per Consulente
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Split 4-way per ogni commissione: <b>Consulente</b> · <b>Coach</b> · <b>Reserve</b> (storni) · <b>Wertia</b>.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={month} onValueChange={setMonth}>
            <SelectTrigger className="w-48 h-9" data-testid="month-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {monthOptions().map((m) => <SelectItem key={m.v} value={m.v}>{m.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button size="sm" onClick={load} disabled={loading} className="bg-prime hover:bg-prime/90 text-white" data-testid="refresh-btn">
            <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Aggiorna
          </Button>
          <Button size="sm" variant="outline" onClick={rebuild} disabled={loading} title="Rigenera tutti gli split dalle commissioni esistenti" data-testid="rebuild-btn">
            Rebuild
          </Button>
        </div>
      </div>

      {/* Totals banner */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <TotalsTile label="Lordo Compagnie" value={t.gross} color="text-stone-900" testId="kpi-gross" />
        <TotalsTile label="Quota Consulenti" value={t.consultant} color="text-emerald-600" testId="kpi-consultant" />
        <TotalsTile label="Quota Coach" value={t.coach} color="text-blue-600" testId="kpi-coach" />
        <TotalsTile label="Reserve (bloccato)" value={t.reserve} color="text-amber-600" testId="kpi-reserve" />
        <TotalsTile label="Cassa Wertia" value={t.wertia} color="text-prime" testId="kpi-wertia" />
      </div>

      {/* Per-consultant rows */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-stone-100 border-b border-stone-200">
                <tr className="text-left">
                  <th className="px-3 py-2 font-mono text-xs uppercase tracking-wider text-stone-600">Consulente</th>
                  <th className="px-3 py-2 font-mono text-xs uppercase tracking-wider text-stone-600">Stufe</th>
                  <th className="px-3 py-2 font-mono text-xs uppercase tracking-wider text-stone-600 text-right">N° Pol.</th>
                  <th className="px-3 py-2 font-mono text-xs uppercase tracking-wider text-stone-600 text-right">Lordo</th>
                  <th className="px-3 py-2 font-mono text-xs uppercase tracking-wider text-stone-600 text-right">Consulente</th>
                  <th className="px-3 py-2 font-mono text-xs uppercase tracking-wider text-stone-600 text-right">Coach</th>
                  <th className="px-3 py-2 font-mono text-xs uppercase tracking-wider text-stone-600 text-right">Reserve</th>
                  <th className="px-3 py-2 font-mono text-xs uppercase tracking-wider text-stone-600 text-right">Wertia</th>
                  <th className="px-3 py-2 font-mono text-xs uppercase tracking-wider text-stone-600 text-right">Residuo</th>
                  <th className="px-3 py-2 font-mono text-xs uppercase tracking-wider text-stone-600 text-center">Status</th>
                </tr>
              </thead>
              <tbody>
                {(data?.rows || []).map((r) => (
                  <React.Fragment key={r.consultant_id}>
                    <tr
                      className="border-b border-stone-100 hover:bg-prime/5 cursor-pointer"
                      onClick={() => openDetail(r.consultant_id)}
                      data-testid={`row-${r.consultant_id}`}
                    >
                      <td className="px-3 py-2.5">
                        {expandedId === r.consultant_id
                          ? <ChevronDown className="w-3.5 h-3.5 inline text-prime mr-1" />
                          : <ChevronRight className="w-3.5 h-3.5 inline text-muted-foreground mr-1" />}
                        <span className="font-medium">{r.full_name}</span>
                      </td>
                      <td className="px-3 py-2.5">
                        <Badge variant="outline" className={`text-[10px] ${LEVEL_COLORS[r.level] || ""}`}>{r.level}</Badge>
                      </td>
                      <td className="px-3 py-2.5 text-right font-mono">{r.n_policies}</td>
                      <td className="px-3 py-2.5 text-right font-mono font-semibold">{fmt(r.gross_total)}</td>
                      <td className="px-3 py-2.5 text-right font-mono text-emerald-700">{fmt(r.consultant_total)}</td>
                      <td className="px-3 py-2.5 text-right font-mono text-blue-700">{fmt(r.coach_total)}</td>
                      <td className="px-3 py-2.5 text-right font-mono text-amber-700">{fmt(r.reserve_total)}</td>
                      <td className="px-3 py-2.5 text-right font-mono text-prime">{fmt(r.wertia_total)}</td>
                      <td className="px-3 py-2.5 text-right font-mono font-bold text-rose-700">{fmt(r.residuo)}</td>
                      <td className="px-3 py-2.5 text-center text-xs">
                        {r.paid_count > 0 && <span className="text-emerald-600 mr-1">✓{r.paid_count}</span>}
                        {r.pending_count > 0 && <span className="text-amber-600 mr-1">⏱{r.pending_count}</span>}
                        {r.not_paid_count > 0 && <span className="text-rose-600">✕{r.not_paid_count}</span>}
                      </td>
                    </tr>
                    {expandedId === r.consultant_id && detail && (
                      <tr><td colSpan={10} className="bg-stone-50 p-3" data-testid={`row-detail-${r.consultant_id}`}>
                        <ConsultantDetail
                          detail={detail}
                          onMarkPaid={(sid, st) => markCompanyPaid(sid, st)}
                          onPayConsultant={(sid) => payConsultant(sid)}
                          onUnlockReserve={(sid) => unlockReserve(sid)}
                        />
                      </td></tr>
                    )}
                  </React.Fragment>
                ))}
                {(!data?.rows || data.rows.length === 0) && (
                  <tr><td colSpan={10} className="px-3 py-12 text-center text-muted-foreground">
                    Nessuna commissione per questo mese.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function TotalsTile({ label, value, color, testId }) {
  return (
    <div className="border border-stone-200 rounded-md px-3 py-2.5 bg-white" data-testid={testId}>
      <div className="text-[10px] text-stone-500 uppercase tracking-wider">{label}</div>
      <div className={`text-xl font-display font-semibold mt-1 font-mono ${color}`}>CHF {fmt(value)}</div>
    </div>
  );
}

function ConsultantDetail({ detail, onMarkPaid, onPayConsultant, onUnlockReserve }) {
  const splits = detail?.splits || [];
  return (
    <div className="space-y-2">
      {splits.length === 0 ? (
        <p className="text-xs text-muted-foreground italic">Nessuno split per questo consulente nel mese selezionato.</p>
      ) : (
        <table className="w-full text-xs">
          <thead className="border-b border-stone-200">
            <tr className="text-left">
              <th className="px-2 py-1.5 font-mono uppercase">Compagnia</th>
              <th className="px-2 py-1.5 font-mono uppercase">Cliente</th>
              <th className="px-2 py-1.5 font-mono uppercase">Polizza</th>
              <th className="px-2 py-1.5 font-mono uppercase text-right">Lordo</th>
              <th className="px-2 py-1.5 font-mono uppercase text-right">Consulente</th>
              <th className="px-2 py-1.5 font-mono uppercase text-right">Reserve</th>
              <th className="px-2 py-1.5 font-mono uppercase text-center">Compagnia</th>
              <th className="px-2 py-1.5 font-mono uppercase text-center">Pagato</th>
              <th className="px-2 py-1.5 font-mono uppercase text-center">Reserve</th>
            </tr>
          </thead>
          <tbody>
            {splits.map((s) => {
              const stMeta = STATUS_META[s.company_payment_status] || STATUS_META.pending;
              const StatusIcon = stMeta.icon;
              return (
                <tr key={s.id} className="border-b border-stone-100">
                  <td className="px-2 py-1.5">{s.company_name}</td>
                  <td className="px-2 py-1.5">{s.client_name}</td>
                  <td className="px-2 py-1.5 font-mono text-prime">{s.policy_number}</td>
                  <td className="px-2 py-1.5 text-right font-mono font-semibold">{fmt(s.gross_amount)}</td>
                  <td className="px-2 py-1.5 text-right font-mono text-emerald-700">{fmt(s.consultant_share)}</td>
                  <td className="px-2 py-1.5 text-right font-mono text-amber-700">{fmt(s.reserve_share)}</td>
                  <td className="px-2 py-1.5 text-center">
                    <Select value={s.company_payment_status} onValueChange={(v) => onMarkPaid(s.id, v)}>
                      <SelectTrigger className={`h-7 text-[10px] ${stMeta.color} w-28`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">In attesa</SelectItem>
                        <SelectItem value="paid">Pagato</SelectItem>
                        <SelectItem value="late">In ritardo</SelectItem>
                        <SelectItem value="not_paid">Non pagato</SelectItem>
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="px-2 py-1.5 text-center">
                    {s.consultant_paid ? (
                      <CheckCircle2 className="w-4 h-4 text-emerald-600 inline" />
                    ) : (
                      <Button size="sm" variant="outline" className="h-6 text-[10px]" onClick={() => onPayConsultant(s.id)}>
                        Paga
                      </Button>
                    )}
                  </td>
                  <td className="px-2 py-1.5 text-center">
                    {s.reserve_unlocked ? (
                      <CheckCircle2 className="w-4 h-4 text-emerald-600 inline" title="Sbloccato" />
                    ) : (
                      <Button size="sm" variant="outline" className="h-6 text-[10px] border-amber-300 text-amber-700" onClick={() => onUnlockReserve(s.id)}>
                        🔓 Sblocca
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
